import {
  DTableProps,
  CombinedText,
  renderDateCell,
  DMenuProps,
  DarwinFlex,
  ActionsDropdown,
  ACTIONS_COL_WIDTH,
  DTableColumnsType,
  ColumnAction,
} from '@darwin-ui';
import Icon from 'components/darwin-ui/Icon';
import { TFunction } from 'i18next';
import { Report } from 'types/reportTypes';
import { translateField, translateFormSelect } from 'utils/common';
import { REPORT_COLUMN_KEYS } from 'constants/reports';

export const getReportsColumns = (
  t: TFunction,
  actions: ColumnAction<Report>[]
): DTableColumnsType<Report> => {
  const menuItems = (transaction: Report): DMenuProps['items'] =>
    actions.map(action => ({
      key: action.key,
      label: (
        <DarwinFlex
          gap={8}
          align="center"
          onClick={() => {
            action?.onClick?.(transaction);
          }}
        >
          <Icon
            name={action.name ? action.name : 'darwin-user'}
            size="sm"
            color="inherit"
          />
          {action.label}
        </DarwinFlex>
      ),
    }));

  const columns: DTableProps<Report>['columns'] = [
    {
      title: t('transactions.columns.traceability-id'),
      dataIndex: REPORT_COLUMN_KEYS.TRACEABILITY_ID,
      key: REPORT_COLUMN_KEYS.TRACEABILITY_ID,
      sorter: true,
      render: (traceabilityId: string) => (
        <CombinedText lines={1} text={traceabilityId} />
      ),
      width: 320,
    },
    {
      title: t('transactions.columns.transaction-date'),
      dataIndex: REPORT_COLUMN_KEYS.TRANSACTION_DATE,
      key: REPORT_COLUMN_KEYS.TRANSACTION_DATE,
      sorter: true,
      render: (transactionDate: string) => (
        <CombinedText lines={1} text={renderDateCell(transactionDate)} />
      ),
    },
    {
      title: t('transactions.columns.event'),
      dataIndex: REPORT_COLUMN_KEYS.EVENT,
      key: REPORT_COLUMN_KEYS.EVENT,
      sorter: true,
      render: (event: string) => <CombinedText lines={1} text={event} />,
    },
    {
      title: t('transactions.columns.product'),
      dataIndex: REPORT_COLUMN_KEYS.PRODUCT,
      key: REPORT_COLUMN_KEYS.PRODUCT,
      sorter: true,
      render: (product: string) => <CombinedText lines={1} text={product} />,
    },
    {
      title: t('transactions.columns.commodity'),
      dataIndex: REPORT_COLUMN_KEYS.COMMODITY,
      key: REPORT_COLUMN_KEYS.COMMODITY,
      sorter: true,
      render: (commodity: string) => (
        <CombinedText lines={1} text={commodity} />
      ),
    },
    {
      title: t('transactions.columns.variety'),
      dataIndex: REPORT_COLUMN_KEYS.VARIETY,
      key: REPORT_COLUMN_KEYS.VARIETY,
      sorter: true,
      render: (variety: string) => <CombinedText lines={1} text={variety} />,
    },
    {
      title: t('transactions.columns.event-date'),
      dataIndex: REPORT_COLUMN_KEYS.EVENT_DATE,
      key: REPORT_COLUMN_KEYS.EVENT_DATE,
      sorter: true,
      render: (eventDate: string) => (
        <CombinedText lines={1} text={renderDateCell(eventDate)} />
      ),
    },
    {
      title: t('transactions.columns.quantity'),
      dataIndex: REPORT_COLUMN_KEYS.QUANTITY,
      key: REPORT_COLUMN_KEYS.QUANTITY,
      sorter: true,
      render: (quantity: number) => (
        <CombinedText lines={1} text={quantity.toString()} />
      ),
      width: 120,
    },
    {
      title: t('transactions.columns.unit-of-measure'),
      dataIndex: REPORT_COLUMN_KEYS.UNIT_OF_MEASURE,
      key: REPORT_COLUMN_KEYS.UNIT_OF_MEASURE,
      sorter: true,
      render: (unitOfMeasure: string) => (
        <CombinedText lines={1} text={translateFormSelect(t, unitOfMeasure)} />
      ),
    },
    {
      title: t('transactions.columns.balance'),
      dataIndex: REPORT_COLUMN_KEYS.BALANCE,
      key: REPORT_COLUMN_KEYS.BALANCE,
      sorter: true,
      render: (balance: string) => <CombinedText lines={1} text={balance} />,
    },
    {
      title: t('transactions.columns.location'),
      dataIndex: REPORT_COLUMN_KEYS.LOCATION,
      key: REPORT_COLUMN_KEYS.LOCATION,
      sorter: true,
      render: (location: string) => <CombinedText lines={1} text={location} />,
    },
    {
      title: t('transactions.columns.reference-document-type'),
      dataIndex: REPORT_COLUMN_KEYS.REFERENCE_DOCUMENT_TYPE,
      key: REPORT_COLUMN_KEYS.REFERENCE_DOCUMENT_TYPE,
      sorter: true,
      render: (_: string, record: Report) => (
        <CombinedText
          lines={1}
          text={
            record.referenceDocumentTypeTranslationKey
              ? t(record.referenceDocumentTypeTranslationKey)
              : translateField(t, record.referenceDocumentType, {
                isTransactionsReferenceDocumentType: true,
              })
          }
        />
      ),
    },
    {
      title: t('transactions.columns.reference-document-number'),
      dataIndex: REPORT_COLUMN_KEYS.REFERENCE_DOCUMENT_NUMBER,
      key: REPORT_COLUMN_KEYS.REFERENCE_DOCUMENT_NUMBER,
      sorter: true,
      render: (referenceDocumentNumber: string) => (
        <CombinedText lines={1} text={referenceDocumentNumber} />
      ),
    },
    {
      title: t('transactions.columns.destination'),
      dataIndex: REPORT_COLUMN_KEYS.DESTINATION,
      key: REPORT_COLUMN_KEYS.DESTINATION,
      sorter: true,
      render: (destination: string) => (
        <CombinedText lines={1} text={destination} />
      ),
    },
    {
      title: t('transactions.columns.traceability-lot-code'),
      dataIndex: REPORT_COLUMN_KEYS.TRACEABILITY_LOT_CODE,
      key: REPORT_COLUMN_KEYS.TRACEABILITY_LOT_CODE,
      sorter: true,
      render: (traceabilityLotCode: string) => (
        <CombinedText lines={1} text={traceabilityLotCode ?? '-'} />
      ),
    },
    {
      title: t('transactions.columns.traceability-lot-code-location'),
      dataIndex: REPORT_COLUMN_KEYS.TRACEABILITY_LOT_CODE_LOCATION,
      key: REPORT_COLUMN_KEYS.TRACEABILITY_LOT_CODE_LOCATION,
      sorter: true,
      render: (traceabilityLotCodeLocation: string) => (
        <CombinedText lines={1} text={traceabilityLotCodeLocation ?? '-'} />
      ),
    },
    {
      title: t('transactions.columns.origin-location'),
      dataIndex: REPORT_COLUMN_KEYS.ORIGIN_LOCATION,
      key: REPORT_COLUMN_KEYS.ORIGIN_LOCATION,
      sorter: true,
      render: (originLocation: string) => (
        <CombinedText lines={1} text={originLocation} />
      ),
    },
    {
      key: REPORT_COLUMN_KEYS.ACTIONS,
      fixed: 'right',
      align: 'center',
      render: (_: any, transaction: Report) => (
        <ActionsDropdown item={transaction} getMenuItems={menuItems} />
      ),
      width: ACTIONS_COL_WIDTH,
    },
  ];

  return columns;
};
